import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "form",
    "input",
    "preview",
    "dropZone",
    "progressBar",
    "status",
    "arrow",
    "photoGrid",
    "photoToggleText",
    "photoToggleIcon",
    "refreshButton",
    "liveToggle",
    "liveStatus",
    "namePrompt",
    "nameInput",
    "uploaderNameDisplay"
  ]

  // Remove these from optionalTargets since they're now required
  static optionalTargets = []

  connect() {
    this.liveEnabled = true
    this.uploadCount = 0
    this.totalFiles = 0
    this.failedUploads = []
    this.uploaderName = this.getCookie("uploaderName")

    // Add input change listener
    if (this.hasInputTarget) {
      console.log("Input target found")
    } else {
      console.error("Input target missing")
    }

    // Add direct upload event listeners to the form
    const form = this.formTarget
    console.log("Form element:", form)

    if (this.hasFormTarget) {
      console.log("Form target found:", this.formTarget)
      this.setupFormListeners()
      this.setupDirectUploadHandlers()
    } else {
      console.error("No form target found!")
    }

    // Set up periodic check for new photos
    if (this.hasPhotoGridTarget) {
      //this.checkNewPhotosInterval = setInterval(() => this.checkForNewPhotos(), 5000)
    }

    if (this.hasFormTarget) {
      // Direct upload events
      this.formTarget.addEventListener("direct-upload:initialize", event => {
        console.log("🚀 Direct upload initialize:", {
          id: event.detail.id,
          file: event.detail.file.name
        })
      })

      this.formTarget.addEventListener("direct-upload:start", event => {
        console.log("📤 Direct upload start:", {
          id: event.detail.id,
          file: event.detail.file.name
        })
      })

      this.formTarget.addEventListener("direct-upload:progress", event => {
        const { id, progress } = event.detail
        console.log(`📊 Upload progress for ${id}: ${progress}%`)
      })

      this.formTarget.addEventListener("direct-upload:error", event => {
        console.error("❌ Upload error:", {
          id: event.detail.id,
          error: event.detail.error
        })
      })

      this.formTarget.addEventListener("direct-upload:end", event => {
        console.log("✅ Upload complete:", {
          id: event.detail.id
        })
      })
    }

    // Handle clicking outside the name prompt
    if (this.hasNamePromptTarget) {
      console.log("Name prompt target found")
      this.namePromptTarget.addEventListener('click', (event) => {
        if (event.target === this.namePromptTarget) {
          this.hideNamePrompt()
        }
      })
    }

    // Only try to set up form listeners if we have a form target
    if (this.hasFormTarget) {
      console.log("Form target found")
      this.setupFormListeners()
    }
  }

  setupFormListeners() {
    if (this.hasFormTarget) {
      console.log("📝 Setting up form listeners")

      // Regular form events
      this.formTarget.addEventListener("submit", (e) => {
        console.log("📝 Native form submit", e)
      })

      // Direct upload events
      this.formTarget.addEventListener("direct-upload:initialize", (e) => {
        console.log("📤 Direct upload initialize", e)
        this.initializeUpload(e)
      })

      this.formTarget.addEventListener("direct-upload:start", (e) => {
        console.log("📤 Direct upload start", e)
        this.startUpload(e)
      })

      this.formTarget.addEventListener("direct-upload:progress", (e) => {
        console.log("📤 Direct upload progress", e)
        this.progressUpload(e)
      })

      this.formTarget.addEventListener("direct-upload:complete", (e) => {
        console.log("📤 Direct upload complete", e)
      })

      this.formTarget.addEventListener("direct-upload:error", (e) => {
        console.log("📤 Direct upload error", e)
        this.errorUpload(e)
      })

      this.formTarget.addEventListener("direct-upload:end", (e) => {
        console.log("📤 Direct upload end", e)
        this.endUpload(e)
      })
    }
  }

  setupDirectUploadHandlers() {
    console.log("Setting up direct upload handlers")

    // Initialize upload
    this.formTarget.addEventListener("direct-upload:initialize", event => {
      console.log("Direct upload initialize", event)
    })

    // Start upload
    this.formTarget.addEventListener("direct-upload:start", event => {
      console.log("Direct upload start", event)
    })

    // Progress
    this.formTarget.addEventListener("direct-upload:progress", event => {
      console.log("Direct upload progress", event)
    })

    // Complete - THIS IS WHERE WE'LL RESET
    this.formTarget.addEventListener("direct-upload:end", event => {
      console.log("Direct upload end - resetting form")
      this.resetUpload()
    })

    // Error handling
    this.formTarget.addEventListener("direct-upload:error", event => {
      console.log("Direct upload error", event)
    })
  }

  disconnect() {
    if (this.checkNewPhotosInterval) {
      clearInterval(this.checkNewPhotosInterval)
    }
  }

  handleSubmit(event) {
    console.log("Form submit handler")
    if (this.hasPreviewTarget) {
      this.previewTarget.innerHTML = ''
      this.previewTarget.classList.add('hidden')
    }
    if (this.hasProgressBarTarget) {
      this.progressBarTarget.classList.remove('hidden')
    }
  }

  handleFiles(event) {
    console.log("📁 handleFiles triggered", event)
    const files = Array.from(event.target.files || [])
    console.log("Files selected:", files)

    if (files.length > 0) {
      // Check for uploader name first
      const uploaderName = this.getCookie("uploaderName")
      console.log("Current uploader name:", uploaderName)

      if (!uploaderName) {
        console.log("No uploader name found, showing prompt")
        this.pendingFiles = files
        if (this.hasNamePromptTarget) {
          this.namePromptTarget.classList.remove('hidden')
          return // Important: return here to prevent further processing
        } else {
          console.error("Name prompt target not found!")
        }
        return
      }

      // Add hidden field for uploader name if it doesn't exist
      if (this.hasFormTarget) {
        let uploaderNameField = this.formTarget.querySelector('input[name="uploader_name"]')
        if (!uploaderNameField) {
          uploaderNameField = document.createElement('input')
          uploaderNameField.type = 'hidden'
          uploaderNameField.name = 'uploader_name'
          this.formTarget.appendChild(uploaderNameField)
        }
        uploaderNameField.value = uploaderName
      }

      // Only proceed with preview if we have a name
      this.previewFiles(files)

      // Hide the arrow if it exists
      if (this.hasArrowTarget) {
        this.arrowTarget.classList.add('hidden')
      }

      // Remove glow animation from drop zone
      if (this.hasDropZoneTarget) {
        this.dropZoneTarget.classList.remove('animate-glow-subtle')
      }
    }
  }

  previewFiles(files) {
    // Clear existing previews
    if (this.hasPreviewTarget) {
      this.previewTarget.innerHTML = ''
    }

    // Create preview grid
    const previewGrid = document.createElement('div')
    previewGrid.className = 'grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 p-4'

    Array.from(files).forEach(file => {
      if (file.type.startsWith('image/')) {
        const reader = new FileReader()
        const preview = document.createElement('div')
        preview.className = 'relative aspect-[4/3] rounded-lg overflow-hidden bg-gray-100'

        reader.onload = (e) => {
          preview.innerHTML = `
            <img src="${e.target.result}"
                 class="w-full h-full object-cover"
                 alt="${file.name}"/>
            <div class="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/50 to-transparent p-2">
              <p class="text-white text-sm truncate">${file.name}</p>
            </div>
          `
        }

        reader.readAsDataURL(file)
        previewGrid.appendChild(preview)
      }
    })

    // Create upload button container
    const buttonContainer = document.createElement('div')
    buttonContainer.className = 'flex justify-end mt-4'
    const uploadButton = document.createElement('button')
    uploadButton.className = 'bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg transition-colors duration-200'
    uploadButton.textContent = `Upload ${files.length} photo${files.length === 1 ? '' : 's'}`
    uploadButton.addEventListener('click', (e) => {
      e.preventDefault()
      // Disable the button immediately
      uploadButton.disabled = true
      uploadButton.classList.add('opacity-50', 'cursor-not-allowed')
      uploadButton.textContent = 'Uploading...'

      console.log("🔘 Upload button clicked")
      if (this.hasFormTarget) {
        console.log("🔘 Form found, submitting...")

        // Add one-time listener for form submission completion
        this.formTarget.addEventListener("turbo:submit-end", () => {
          console.log("🎯 Upload complete via one-time listener")
          this.resetUpload()
        }, { once: true })

        this.formTarget.requestSubmit()
      }
    })
    buttonContainer.appendChild(uploadButton)

    if (this.hasPreviewTarget) {
      this.previewTarget.appendChild(previewGrid)
      this.previewTarget.appendChild(buttonContainer)
      this.previewTarget.classList.remove('hidden')
    }
  }

  startUpload(event) {
    console.log('startUpload event', event)
    const { id } = event.detail
    const element = this.progressBarTarget.querySelector(`[data-direct-upload-id="${id}"]`)
    if (element) {
      element.querySelector('.status').textContent = "Uploading..."
    }
  }

  progressUpload(event) {
    console.log("progress event", event)
    const { id, progress } = event.detail
    const element = this.progressBarTarget.querySelector(`[data-direct-upload-id="${id}"]`)
    if (element) {
      const progressBar = element.querySelector('.bg-blue-600')
      const statusElement = element.querySelector('.status')
      if (progressBar && statusElement) {
        progressBar.style.width = `${progress}%`
        statusElement.textContent = `${Math.round(progress)}%`
      }
    }
  }

  errorUpload(event) {
    const { id, error } = event.detail
    const element = this.progressBarTarget.querySelector(`[data-direct-upload-id="${id}"]`)
    if (element) {
      element.querySelector('.status').textContent = `Error: ${error}`
      element.classList.add('direct-upload--error')
    }
  }

  endUpload(event) {
    const { id } = event.detail
    const element = this.progressBarTarget.querySelector(`[data-direct-upload-id="${id}"]`)
    if (element) {
      element.querySelector('.status').textContent = "Processing..."
    }
  }

  createFileProgressElement(file) {
    const element = document.createElement('div')
    element.className = "file-progress"
    element.dataset.directUploadId = file.upload.id
    element.innerHTML = `
      <div class="flex justify-between text-sm mb-1">
        <span class="truncate">${file.name}</span>
        <span class="status">Preparing...</span>
      </div>
      <div class="bg-gray-200 rounded-full h-1.5">
        <div class="bg-blue-600 h-1.5 rounded-full transition-all duration-300" style="width: 0%"></div>
      </div>
    `
    return element
  }

  initializeUpload(event) {
    const { target, detail } = event
    const { id, file } = detail
    const progressElement = this.createFileProgressElement(file)
    this.progressBarTarget.classList.remove('hidden')
    this.progressBarTarget.querySelector('.space-y-4').appendChild(progressElement)
  }

  dragOver(event) {
    event.preventDefault()
    this.dropZoneTarget.classList.add('dragging')
  }

  dragLeave(event) {
    event.preventDefault()
    this.dropZoneTarget.classList.remove('dragging')
  }

  drop(event) {
    event.preventDefault()
    this.dropZoneTarget.classList.remove('dragging')

    const files = event.dataTransfer.files
    if (files.length > 0) {
      this.inputTarget.files = files
      const changeEvent = new Event('change', { bubbles: true })
      this.inputTarget.dispatchEvent(changeEvent)
    }
  }

  preview(event) {
    console.log("Preview started", event.target.files)
    this.startUpload(event)
    this.previewFiles(event.target.files)
  }

  toggleLive() {
    this.liveEnabled = !this.liveEnabled

    if (this.liveEnabled) {
      this.liveStatusTarget.textContent = "Live Updates On"
      this.liveToggleTarget.querySelector('.icon').textContent = "🔴"
    } else {
      this.liveStatusTarget.textContent = "Live Updates Off"
      this.liveToggleTarget.querySelector('.icon').textContent = "⚫"
    }
  }

  clearForm() {
    this.submitButtonTarget.value = this.submitButtonTarget.dataset.defaultText
    this.updateUIState()
  }

  updateUIState() {
    const hasFiles = this.inputTarget.files.length > 0

    // Show/hide arrow
    if (this.hasArrowTarget) {
      this.arrowTarget.classList.toggle('hidden', hasFiles)
    }

    // Toggle glow effect
    this.dropZoneTarget.classList.toggle('animate-glow-subtle', !hasFiles)
  }

  triggerFileInput(event) {
    event.preventDefault()
    console.log("Triggering file input")
    if (this.hasInputTarget) {
      this.inputTarget.click()
    } else {
      console.error("No input target found")
    }
  }

  resetUpload() {
    console.log("🧹 Resetting upload form")

    // Clear file input
    if (this.hasInputTarget) {
      console.log("Clearing input")
      this.inputTarget.value = ''
    }

    // Clear and hide previews
    if (this.hasPreviewTarget) {
      console.log("Clearing preview")
      this.previewTarget.innerHTML = ''
      this.previewTarget.classList.add('hidden')
    }

    // Reset and hide progress bar
    if (this.hasProgressBarTarget) {
      console.log("Resetting progress bar")
      this.progressBarTarget.classList.add('hidden')
      const progressBar = this.progressBarTarget.querySelector('.bg-blue-600')
      if (progressBar) {
        progressBar.style.width = '0%'
      }
      const progressContainer = this.progressBarTarget.querySelector('.space-y-4')
      if (progressContainer) {
        progressContainer.innerHTML = ''
      }
    }

    // Show arrow again
    if (this.hasArrowTarget) {
      console.log("Showing arrow")
      this.arrowTarget.classList.remove('hidden')
    }

    // Add back the glow animation to drop zone
    if (this.hasDropZoneTarget) {
      console.log("Resetting drop zone")
      this.dropZoneTarget.classList.add('animate-glow-subtle')
    }
  }

  uploadComplete(event) {
    console.log("Upload complete")
    this.resetUpload()
    if (this.hasStatusTarget) {
      this.statusTarget.textContent = "Upload complete!"
      // Clear the "Upload complete!" message after 3 seconds
      setTimeout(() => {
        this.statusTarget.textContent = ''
      }, 3000)
    }
  }

  togglePhotos(event) {
    event.preventDefault()

    if (!this.hasPhotoGridTarget || !this.hasPhotoToggleTextTarget || !this.hasPhotoToggleIconTarget) {
      console.error("Missing required targets for photo toggle")
      return
    }

    const isHidden = this.photoGridTarget.classList.contains('hidden')

    if (isHidden) {
      this.photoGridTarget.classList.remove('hidden')
      this.photoToggleIconTarget.textContent = '👁️‍🗨️'
      this.photoToggleTextTarget.textContent = 'Hide Photos'
    } else {
      this.photoGridTarget.classList.add('hidden')
      this.photoToggleIconTarget.textContent = '👁️'
      this.photoToggleTextTarget.textContent = 'Show Photos'
    }
  }

  saveName(event) {
    event.preventDefault()
    const name = this.nameInputTarget.value.trim()
    if (name) {
      console.log("Saving name:", name)
      this.setCookie("uploaderName", name, 365)
      this.uploaderName = name

      // Update the display
      if (this.hasUploaderNameDisplayTarget) {
        this.uploaderNameDisplayTarget.textContent = name
      }

      // Add or update the hidden field for uploader name
      if (this.hasFormTarget) {
        let uploaderNameField = this.formTarget.querySelector('input[name="uploader_name"]')
        if (!uploaderNameField) {
          uploaderNameField = document.createElement('input')
          uploaderNameField.type = 'hidden'
          uploaderNameField.name = 'uploader_name'
          this.formTarget.appendChild(uploaderNameField)
        }
        uploaderNameField.value = name
      }

      this.namePromptTarget.classList.add('hidden')

      // Handle pending files if they exist
      if (this.pendingFiles) {
        this.previewFiles(this.pendingFiles)
        this.pendingFiles = null
      }
    }
  }

  getCookie(name) {
    const value = `; ${document.cookie}`
    const parts = value.split(`; ${name}=`)
    if (parts.length === 2) return parts.pop().split(';').shift()
  }

  setCookie(name, value, days) {
    const date = new Date()
    date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000))
    document.cookie = `${name}=${value};expires=${date.toUTCString()};path=/`
  }

  initialize() {
    if (this.hasFormTarget) {
      console.log('HAS FORM TARGET');
      this.formTarget.addEventListener("turbo:submit-start", () => {
        console.log("Upload starting...")
        this.progressBarTarget.classList.remove('hidden')
      })

      this.formTarget.addEventListener("turbo:before-fetch-request", () => {
        console.log("Preparing upload...")
      })

      this.formTarget.addEventListener("turbo:upload-progress", (event) => {
        console.log("Upload progress:", event.detail)
        const [total, loaded] = event.detail.fetchResponse.uploadProgress
        const progress = (loaded / total) * 100

        // Update progress bar
        const progressBar = this.progressBarTarget.querySelector('.space-y-4')
        if (progressBar) {
          progressBar.innerHTML = `
            <div class="flex justify-between text-sm mb-1">
              <span>Uploading...</span>
              <span>${Math.round(progress)}%</span>
            </div>
            <div class="bg-gray-200 rounded-full h-1.5">
              <div class="bg-blue-600 h-1.5 rounded-full transition-all duration-300"
                   style="width: ${progress}%"></div>
            </div>
          `
        }
      })

      this.formTarget.addEventListener("turbo:submit-end", () => {
        console.log("Upload complete")
        setTimeout(() => {
          this.progressBarTarget.classList.add('hidden')
        }, 1000)
      })
    }
  }

  async checkForNewPhotos() {
    const currentCount = parseInt(this.photoGridTarget.dataset.photosCount || '0')
    const response = await fetch(window.location.pathname + '/photos/count')

    if (response.ok) {
      const { count } = await response.json()
      if (count > currentCount) {
        this.refreshButtonTarget.classList.remove('hidden')
      } else {
        this.refreshButtonTarget.classList.add('hidden')
      }
    }
  }

  async refreshPhotos(event) {
    event.preventDefault()

    // Show loading state
    const button = event.currentTarget
    const originalText = button.innerHTML
    button.innerHTML = '<span>🔄</span><span>Loading...</span>'
    button.disabled = true

    try {
      const response = await fetch(window.location.pathname + '/photos', {
        headers: {
          'Accept': 'text/vnd.turbo-stream.html'
        }
      })

      if (response.ok) {
        const html = await response.text()
        Turbo.renderStreamMessage(html)
        this.refreshButtonTarget.classList.add('hidden')

        // If photos were hidden, show them
        this.photoGridTarget.classList.remove('hidden')
        this.photoToggleIconTarget.textContent = '👁️‍🗨️'
        this.photoToggleTextTarget.textContent = 'Hide Photos'
      }
    } catch (error) {
      console.error('Error refreshing photos:', error)
    } finally {
      // Reset button state
      button.innerHTML = originalText
      button.disabled = false
    }
  }

  handleBeforeFetch(event) {
    console.log("Before fetch", event)
    if (this.hasProgressBarTarget) {
      this.progressBarTarget.classList.remove('hidden')
    }
  }

  handleUploadStart(event) {
    console.log("Upload started", event)
    if (this.hasPreviewTarget) {
      this.previewTarget.innerHTML = ''
    }
  }

  handleUploadProgress(event) {
    console.log("Upload progress", event)
    if (!this.hasProgressBarTarget) return

    const [loaded, total] = event.detail.fetchResponse.uploadProgress
    const progress = (loaded / total) * 100

    const progressBar = this.progressBarTarget.querySelector('.space-y-4')
    if (progressBar) {
      progressBar.innerHTML = `
        <div class="flex justify-between text-sm mb-1">
          <span>Uploading...</span>
          <span>${Math.round(progress)}%</span>
        </div>
        <div class="bg-gray-200 rounded-full h-1.5">
          <div class="bg-blue-600 h-1.5 rounded-full transition-all duration-300"
               style="width: ${progress}%"></div>
        </div>
      `
    }
  }

  handleUploadComplete(event) {
    console.log("Upload complete", event)
    if (this.hasProgressBarTarget) {
      setTimeout(() => {
        this.progressBarTarget.classList.add('hidden')
      }, 1000)
    }
    if (this.hasPreviewTarget) {
      this.previewTarget.innerHTML = ''
    }
    // Clear the file input
    if (this.hasInputTarget) {
      this.inputTarget.value = ''
    }
  }

  handleUploadClick(e) {
    e.preventDefault()
    console.log("🔘 Upload button clicked")
    if (this.hasFormTarget) {
      const formData = new FormData(this.formTarget)
      formData.append('uploader_name', this.getCookie('uploaderName') || 'Anonymous')

      console.log("Form submission details:", {
        method: this.formTarget.method,
        action: this.formTarget.action,
        enctype: this.formTarget.enctype,
        turboEnabled: this.formTarget.dataset.turbo === "true"
      })
    }
  }

  showNamePrompt(event) {
    event?.preventDefault()
    if (this.hasNamePromptTarget && this.hasNameInputTarget) {
      // Pre-fill with current name if it exists
      const currentName = this.getCookie("uploaderName")
      if (currentName) {
        this.nameInputTarget.value = currentName
      }

      this.namePromptTarget.classList.remove('hidden')
      // Focus the input after showing
      setTimeout(() => this.nameInputTarget.focus(), 100)
    }
  }

  hideNamePrompt() {
    if (this.hasNamePromptTarget) {
      this.namePromptTarget.classList.add('hidden')
      // Clear pending files if we're canceling
      this.pendingFiles = null
    }
  }

  handleNameInputKeyup(event) {
    if (event.key === 'Enter') {
      this.saveName(event)
    }
  }
}
